import React, { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { PaperclipIcon } from "lucide-react";
import styled from "styled-components/macro";

import FileUploadQuestion from "../../../components/FileUploadQuestion";
import { TextareaQuestion } from "../../../components/TextQuestion";
import ScrollToTop from "../../../components/utils/ScrollToTop";
import { Tray, TrayActions } from "../../../probate/components/tray";
import { Button } from "../../../probate/components/ui/button";
import { getFormattedDate } from "../../../utils/textUtils";
import { sanitiseHtml } from "../../../utils/sanitiseHtml";
import { enquiryConversation, looksLikeHtml } from "../EnquiryDoable";
import { useCaseFromPath } from "../utils/hooks";
import ProbatePage from "./ProbatePage";
import { enquiryStatus } from "./ProbateMessages";
import { MessageText } from "./Sheet";

const StatusLine = styled.span`
  display: inline-flex;
  gap: var(--juno-space-2);
`;

const Muted = styled.span`
  color: var(--muted-foreground);
`;

const Conversation = styled.section`
  display: flex;
  flex-direction: column;
  padding: 0 var(--juno-space-6);
`;

// With the sheet's 16px gap, the tray sits 24px under the conversation
const ReplyForm = styled.form`
  margin-top: var(--juno-space-2);
`;

const Message = styled.article`
  display: flex;
  flex-direction: column;
  gap: var(--juno-space-2);
  padding-top: var(--juno-space-6);

  &:first-child {
    padding-top: 0;
  }
`;

// A rule under each of Juno's messages that has another after it
const JunoMessage = styled(Message)`
  &:not(:last-child) {
    padding-bottom: var(--juno-space-6);
    border-bottom: 1px solid var(--border);
  }
`;

// The client's replies in a panel, their text kept at the 40px inset
const ClientMessage = styled(Message)`
  margin: var(--juno-space-6) calc(-1 * var(--juno-space-4)) 0;
  padding: var(--juno-space-4);
  border-radius: var(--radius-xl);
  background-color: var(--muted);

  &:first-child {
    margin-top: 0;
    padding-top: var(--juno-space-4);
  }
`;

const Label = styled.p`
  display: flex;
  gap: var(--juno-space-2);
  margin: 0;
  font-size: var(--text-sm);
  line-height: var(--text-sm--line-height);
`;

const Sender = styled.span`
  font-weight: var(--juno-font-weight-bold);
`;

const PlainText = styled.p`
  white-space: pre-line;
`;

// 16px between the reply box and the tray's edge and button, as a request's task has
const ReplyContent = styled.div`
  --question-margin-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: var(--juno-space-4);
  padding: var(--juno-space-4) 0;
`;

// A text button at the reply's edge, its 44px height the tap target
const AttachButton = styled.button`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: var(--juno-space-2);
  min-height: var(--button-height);
  padding: 0;
  border: 0;
  background: none;
  color: var(--juno-color-button);
  font: inherit;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
  }

  svg {
    width: var(--juno-space-5);
    height: var(--juno-space-5);
  }
`;

const ReplyError = styled.p`
  margin: 0;
  padding-bottom: var(--juno-space-4);
  color: var(--destructive);
`;

// 16px above the button and 8px below it, inside the sheet's 16px
const AnotherReply = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--juno-space-4) 0 var(--juno-space-2);
`;

const AttachmentList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    gap: var(--juno-space-2);
  }

  svg {
    width: var(--juno-space-4);
    height: var(--juno-space-4);
    flex-shrink: 0;
  }
`;

const Attachments = ({ files }) =>
  files?.length > 0 ? (
    <AttachmentList>
      {files.map((file, index) => {
        const name = file.display_name || file.filename;
        return (
          <li key={index}>
            <PaperclipIcon aria-hidden />
            {file.link ? (
              <a href={file.link} target="_blank" rel="noreferrer">
                {name}
              </a>
            ) : (
              name
            )}
          </li>
        );
      })}
    </AttachmentList>
  ) : null;

// The original question, then each update, as one thread
const enquiryThread = (doable) => [
  {
    type: "update_to_client",
    created_at: doable.created_at,
    content: doable.configuration.question,
    attachments: doable.configuration.attachments,
  },
  ...enquiryConversation(doable),
];

const JunoText = ({ content }) =>
  looksLikeHtml(content) ? (
    <MessageText dangerouslySetInnerHTML={{ __html: sanitiseHtml(content) }} />
  ) : (
    <MessageText>
      <PlainText>{content}</PlainText>
    </MessageText>
  );

// Blank lines split a reply into paragraphs, as ClientReply does on the conveyancing page
const ClientText = ({ content }) => (
  <MessageText>
    {(content ?? "")
      .trim()
      .split(/\n{2,}/)
      .map((paragraph, index) => (
        <PlainText key={index}>{paragraph}</PlainText>
      ))}
  </MessageText>
);

const sendReply = (doable, reply, documents) =>
  fetch(`/api/dashboard/doable/${doable.id}`, {
    method: "PUT",
    credentials: "same-origin",
    body: JSON.stringify({
      enquiry_update: { client_reply: reply },
      uploaded_documents: documents.map((upload) => ({ identifier: "enquiry_response", upload_id: upload.upload_id })),
    }),
  }).then((response) => {
    if (!response.ok) {
      throw new Error("Non-success response");
    }
  });

const ProbateEnquiry = ({ doable, submissionEnabled }) => {
  const caseRef = useCaseFromPath()?.case_reference;
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sent, setSent] = useState([]);
  const [writing, setWriting] = useState(doable.status === "available");
  const markedRead = useRef(false);

  // The ref keeps StrictMode's second effect run from sending it twice
  useEffect(() => {
    if (markedRead.current || !submissionEnabled || doable.status !== "available_to_read") return;
    markedRead.current = true;
    fetch(`/api/dashboard/doable/${doable.id}`, {
      method: "PUT",
      credentials: "same-origin",
      body: JSON.stringify({ enquiry_update: { mark_as_read: true } }),
    });
  }, [doable.id, doable.status, submissionEnabled]);

  const thread = [...enquiryThread(doable), ...sent].filter((update) => update.content || update.attachments?.length);
  const latest = thread[thread.length - 1];
  // An earlier reply leaves the submission in place when a follow-up needs another
  const replied =
    sent.length > 0 || (doable.status === "complete" && !!doable.submission?.enquiry_update?.client_reply);
  const status = replied ? "Replied" : enquiryStatus[doable.status];

  const submit = (event) => {
    event.preventDefault();
    setSending(true);
    setFailed(false);
    sendReply(doable, reply, documents)
      .then(() => {
        setSent([
          ...sent,
          { type: "update_from_client", created_at: new Date().toISOString(), content: reply, attachments: documents },
        ]);
        setReply("");
        setDocuments([]);
        setAttaching(false);
        setWriting(false);
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags: { action: "ProbateEnquirySubmission", caseRef, doableId: doable.id },
        });
        setFailed(true);
      })
      .finally(() => setSending(false));
  };

  return (
    <ProbatePage
      caseRef={caseRef}
      title={doable.title}
      details={
        <StatusLine>
          {status && <Sender>{status}</Sender>}
          {latest?.created_at && <Muted>{getFormattedDate(latest.created_at)}</Muted>}
        </StatusLine>
      }
      done={sent.length > 0}
      doneLabel="We’ve got your reply"
    >
      <ScrollToTop />
      <Conversation aria-label="Conversation">
        {thread.map((update, index) => {
          const fromClient = update.type === "update_from_client";
          const Wrapper = fromClient ? ClientMessage : JunoMessage;
          return (
            <Wrapper key={index}>
              <Label>
                <Sender>{fromClient ? "You" : "Juno"}</Sender>
                {update.created_at && <Muted>{getFormattedDate(update.created_at)}</Muted>}
              </Label>
              {update.content &&
                (fromClient ? <ClientText content={update.content} /> : <JunoText content={update.content} />)}
              <Attachments files={update.attachments} />
            </Wrapper>
          );
        })}
      </Conversation>
      {!writing && submissionEnabled && (
        <AnotherReply>
          <Button variant="outline" onClick={() => setWriting(true)}>
            {replied ? "Send another reply" : "Send a reply"}
          </Button>
        </AnotherReply>
      )}
      {writing && (
        <ReplyForm onSubmit={submit}>
          {/* The reply tray has no TrayHeader, so it drops the label Tray points at one */}
          <Tray variant="outlined" aria-labelledby={undefined}>
            <ReplyContent>
              <TextareaQuestion
                question="Your reply"
                value={reply}
                valueEntered={setReply}
                disabled={!submissionEnabled}
              />
              {attaching ? (
                <FileUploadQuestion
                  question="Attach documents to your reply"
                  multiple={true}
                  caseRef={caseRef}
                  doableId={doable.id}
                  onUploadsChanged={setDocuments}
                  onUploadInProgress={setUploading}
                />
              ) : (
                <AttachButton type="button" onClick={() => setAttaching(true)}>
                  <PaperclipIcon aria-hidden />
                  Attach a document
                </AttachButton>
              )}
            </ReplyContent>
            {failed && (
              <ReplyError role="alert">
                We couldn’t send your reply. Try again, or send us a message using web chat.
              </ReplyError>
            )}
            <TrayActions>
              <Button type="submit" variant="secondary" disabled={!submissionEnabled || !reply || sending || uploading}>
                Send reply
              </Button>
            </TrayActions>
          </Tray>
        </ReplyForm>
      )}
    </ProbatePage>
  );
};

export default ProbateEnquiry;
